import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Search,
  Edit,
  Trash2,
  Building2,
  TrendingUp,
  Eye,
  DollarSign,
  Target,
  Loader2,
  ArrowLeft,
  User,
  FileText,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { supabase } from "@/lib/supabaseClient";

interface ProjectDeal {
  id: string;
  name: string;
  currency: string;
  created_at: string;
  updated_at: string;
  owner_id: string;
  kpis: any;
  profiles: {
    email: string;
    full_name: string;
  } | null;
}

interface ProcessedDeal {
  id: string;
  name: string;
  currency: string;
  createdAt: string;
  updatedAt: string;
  totalCost: number;
  irr: number;
  rooms: number;
  adr: number;
  occ: number;
  revpar: number;
}

export default function UserProjects() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");

  const [deals, setDeals] = useState<ProcessedDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedDeal, setSelectedDeal] = useState<ProcessedDeal | null>(null);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    if (!userId) {
      navigate("/admin/users");
      return;
    }
    loadDeals();
  }, [userId]);

  const processDeal = (item: ProjectDeal): ProcessedDeal => {
    const kpis = item.kpis || {};
    const totalCost = kpis.purchasePrice || kpis.purchase?.price || 0;
    const projectIRR = kpis.ramp?.revenueGrowthY3Plus / 100 || 0;

    return {
      id: item.id,
      name: item.name || "Untitled Project",
      currency: item.currency || "EUR",
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      totalCost: totalCost,
      irr: projectIRR,
      rooms: kpis.rooms?.total || 0,
      adr: kpis.roomRev?.adr?.[0] || 0,
      occ: kpis.roomRev?.occ?.[0] || 0,
      revpar:
        ((kpis.roomRev?.adr?.[0] || 0) * (kpis.roomRev?.occ?.[0] || 0)) / 100,
    };
  };

  const loadDeals = async () => {
    try {
      setLoading(true);

      const { data, error } = await supabase
        .from("projects")
        .select(
          `
          id,
          name,
          currency,
          created_at,
          updated_at,
          owner_id,
          kpis,
          profiles:owner_id (
            email,
            full_name
          )
        `,
        )
        .eq("owner_id", userId)
        .order("created_at", { ascending: false });

      if (error) throw error;

      const processedDeals = (data || []).map((item: any) => {
        const profile = Array.isArray(item.profiles)
          ? item.profiles[0]
          : item.profiles;
        return processDeal({
          ...item,
          profiles: profile || null,
        });
      });

      setDeals(processedDeals);

      if (data && data.length > 0) {
        const firstProfile = Array.isArray(data[0].profiles)
          ? data[0].profiles[0]
          : data[0].profiles;

        if (firstProfile) {
          setUserName(firstProfile.full_name || "User");
          setUserEmail(firstProfile.email || "");
        }
      }
    } catch (error) {
      console.error("Failed to load deals:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  const filteredDeals = deals.filter((d) => {
    const name = (d.name || "").toLowerCase();
    const searchLower = searchQuery.toLowerCase();
    return name.includes(searchLower);
  });

  const totalDeals = deals.length;
  const avgCost =
    deals.reduce((sum, d) => sum + (d.totalCost || 0), 0) /
    Math.max(deals.length, 1);
  const totalRooms = deals.reduce((sum, d) => sum + (d.rooms || 0), 0);
  const avgIRR =
    deals.reduce((sum, d) => sum + (d.irr || 0), 0) / Math.max(deals.length, 1);

  const formatPrice = (
    price: number | undefined,
    currency: string | undefined,
  ) => {
    if (!price) return `${currency || ""} 0`;
    if (price >= 1000000)
      return `${currency || ""} ${(price / 1000000).toFixed(1)}M`;
    if (price >= 1000) return `${currency || ""} ${(price / 1000).toFixed(0)}K`;
    return `${currency || ""} ${price.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleView = (deal: ProcessedDeal) => {
    navigate(`/admin/deals/${deal.id}`);
  };

  const handleDelete = (deal: ProcessedDeal) => {
    setSelectedDeal(deal);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedDeal) return;

    try {
      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", selectedDeal.id);

      if (error) throw error;

      setDeals(deals.filter((d) => d.id !== selectedDeal.id));
      toast.success(`"${selectedDeal.name}" deleted`);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete project");
    } finally {
      setDeleteDialogOpen(false);
      setSelectedDeal(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        <div className="relative overflow-hidden rounded-lg border bg-gradient-to-br from-blue-50 via-white to-purple-50 p-8">
          <div className="absolute top-0 right-0 w-64 h-64 bg-blue-100 rounded-full -mr-32 -mt-32 opacity-20"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-purple-100 rounded-full -ml-24 -mb-24 opacity-20"></div>

          <div className="relative">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/admin/users")}
              className="mb-4 -ml-2 hover:bg-white/50"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>

            <div className="flex items-start justify-between">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                    {userName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h1 className="text-3xl font-bold tracking-tight text-gray-900">
                      {userName}'s Projects
                    </h1>
                    <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                      <div className="flex items-center gap-1.5">
                        <User className="h-4 w-4" />
                        <span>{userEmail}</span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <FileText className="h-4 w-4" />
                        <span>
                          {totalDeals}{" "}
                          {totalDeals === 1 ? "project" : "projects"}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="bg-white/80 backdrop-blur-sm hover:bg-white"
                  onClick={loadDeals}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500 hover:shadow-md transition-shadow">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Total Projects
                  </p>
                  <p className="text-2xl font-bold">{totalDeals}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500 hover:shadow-md transition-shadow">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Rooms</p>
                  <p className="text-2xl font-bold">{totalRooms}</p>
                </div>
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Target className="h-5 w-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500 hover:shadow-md transition-shadow">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg. Cost</p>
                  <p className="text-2xl font-bold">
                    {formatPrice(avgCost, deals[0]?.currency || "EUR")}
                  </p>
                </div>
                <div className="p-3 bg-amber-100 rounded-xl">
                  <DollarSign className="h-5 w-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-violet-500 hover:shadow-md transition-shadow">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg. IRR</p>
                  <p className="text-2xl font-bold">
                    {(avgIRR * 100).toFixed(1)}%
                  </p>
                </div>
                <div className="p-3 bg-violet-100 rounded-xl">
                  <TrendingUp className="h-5 w-5 text-violet-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search projects..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="text-sm text-muted-foreground">
            {filteredDeals.length} of {totalDeals} projects
          </div>
        </div>

        <Card>
          <CardContent className="pt-6">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Project Name</TableHead>
                  <TableHead>Performance</TableHead>
                  <TableHead className="text-center">Details</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead className="text-center">Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredDeals.map((deal) => (
                  <TableRow key={deal.id} className="hover:bg-gray-50/50">
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                          <Building2 className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">
                            {deal.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {deal.currency}
                          </p>
                        </div>
                      </div>
                    </TableCell>

                    <TableCell>
                      <div className="space-y-1">
                        <Badge
                          variant="outline"
                          className="bg-green-50 text-green-700 border-green-200"
                        >
                          IRR: {(deal.irr * 100).toFixed(1)}%
                        </Badge>
                        <div className="text-xs text-gray-500">
                          RevPAR: {deal.currency} {deal.revpar.toFixed(0)}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="text-center">
                      <div className="space-y-1">
                        <div className="font-medium">{deal.rooms} rooms</div>
                        <div className="text-xs text-gray-500">
                          ADR: {deal.currency} {deal.adr.toFixed(0)}
                        </div>
                        <div className="text-xs text-gray-500">
                          OCC: {deal.occ.toFixed(1)}%
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="font-bold text-gray-900">
                        {formatPrice(deal.totalCost, deal.currency)}
                      </div>
                      {deal.rooms > 0 && (
                        <div className="text-xs text-gray-500">
                          {formatPrice(
                            deal.totalCost / deal.rooms,
                            deal.currency,
                          )}{" "}
                          per key
                        </div>
                      )}
                    </TableCell>

                    <TableCell className="text-center">
                      <div className="text-sm">
                        {formatDate(deal.createdAt)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Updated: {formatDate(deal.updatedAt)}
                      </div>
                    </TableCell>

                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleView(deal)}
                          className="h-8 px-3 text-gray-600 hover:text-blue-600"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(deal)}
                          className="h-8 px-3 text-gray-600 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {filteredDeals.length === 0 && (
              <div className="text-center py-12">
                <div className="mx-auto max-w-xs">
                  <div className="rounded-full bg-gray-100 p-4 w-fit mx-auto">
                    <Building2 className="h-8 w-8 text-gray-400" />
                  </div>
                  <h4 className="mt-4 text-lg font-semibold text-gray-900">
                    No projects yet
                  </h4>
                  <p className="mt-2 text-gray-600">
                    This user hasn't created any projects yet.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-600" />
              Delete Project
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedDeal?.name}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete Project
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
